/**
 * What the app-wide text context menu may offer for one right-click.
 *
 * The app suppresses the webview's native menu everywhere (it would show
 * "Reload" / "Inspect" and the OS look), so without a replacement a right-click
 * on selected text or in a field did nothing at all (owner, 28.09.2026). The
 * replacement is `components/ui/TextContextMenu`; this file is its pure half:
 * given the element that was right-clicked and the current selection, which of
 * Cut / Copy / Paste / Select all apply, and on what.
 *
 * Result shape (null = show no menu at all):
 *   {
 *     kind: 'field' | 'editable' | 'text',
 *     el,        // the input/textarea or contenteditable host (null for 'text')
 *     text,      // what Copy/Cut would put on the clipboard
 *     start, end // field selection offsets (fields only)
 *     range,     // a cloned Range of the selection (contenteditable only)
 *     actions: { copy, cut?, paste?, selectAll? }
 *   }
 * A key present in `actions` means the row is SHOWN; its boolean says whether
 * it is enabled. Plain page text only ever gets Copy.
 */

/** Monaco and xterm bring their own menus and clipboard handling. */
export const OPT_OUT_SELECTOR = '.monaco-editor, .xterm';

/** <input> types that hold editable text with a selection. */
export const TEXT_INPUT_TYPES = new Set(['', 'text', 'search', 'url', 'tel', 'email', 'password']);

function asElement(node) {
  if (!node) return null;
  return node.nodeType === 1 ? node : node.parentElement || null;
}

export function isTextField(el) {
  if (!el) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  return TEXT_INPUT_TYPES.has(String(el.getAttribute('type') || '').toLowerCase());
}

/**
 * The nearest contenteditable host, honouring `contenteditable="false"`
 * islands. Done by attribute rather than `isContentEditable`, which jsdom does
 * not implement.
 */
export function closestEditable(el) {
  const host = el?.closest?.('[contenteditable]');
  if (!host) return null;
  const v = String(host.getAttribute('contenteditable')).toLowerCase();
  return v === 'false' ? null : host;
}

function fieldModel(field) {
  const value = String(field.value ?? '');
  let start = 0;
  let end = 0;
  try {
    start = field.selectionStart ?? 0;
    end = field.selectionEnd ?? 0;
  } catch {
    // Some input types throw on selection access; treat as "no selection".
  }
  const text = value.slice(start, end);
  const secret = String(field.getAttribute('type') || '').toLowerCase() === 'password';
  const locked = Boolean(field.readOnly || field.disabled);
  const hasSel = text.length > 0;
  const actions = {
    cut: hasSel && !secret && !locked,
    copy: hasSel && !secret,
    paste: !locked,
    selectAll: value.length > 0,
  };
  // A password field never leaves its value on the clipboard — not via a menu.
  return { kind: 'field', el: field, text: secret ? '' : text, start, end, range: null, actions };
}

function selectionInside(selection, host) {
  if (!selection || !selection.rangeCount) return false;
  return host.contains(selection.anchorNode) && host.contains(selection.focusNode);
}

function editableModel(host, selection) {
  const inside = selectionInside(selection, host);
  const text = inside ? String(selection.toString()) : '';
  const range = inside ? selection.getRangeAt(0).cloneRange() : null;
  const hasSel = text.length > 0;
  return {
    kind: 'editable',
    el: host,
    text,
    start: 0,
    end: 0,
    range,
    actions: { cut: hasSel, copy: hasSel, paste: true, selectAll: true },
  };
}

/** Does the selection cover (at least part of) the element that was clicked? */
export function selectionTouches(selection, el) {
  if (!selection || !selection.rangeCount || !el) return false;
  // Range.intersectsNode rather than Selection.containsNode(el, true): the
  // latter's partial-containment answer differs between engines (jsdom says
  // yes for a sibling that follows the selection).
  for (let i = 0; i < selection.rangeCount; i += 1) {
    const range = selection.getRangeAt(i);
    try {
      if (range.intersectsNode(el)) return true;
    } catch {
      // detached node — not ours
    }
  }
  return false;
}

function plainModel(el, selection) {
  const text = selection ? String(selection.toString()) : '';
  if (!text.trim()) return null;
  // Right-clicking somewhere else while text is selected elsewhere is not a
  // request to copy that text.
  if (!selectionTouches(selection, el)) return null;
  return { kind: 'text', el: null, text, start: 0, end: 0, range: null, actions: { copy: true } };
}

/**
 * @param {EventTarget} target     the contextmenu event's target
 * @param {Selection|null} selection window.getSelection()
 * @returns {object|null}
 */
export function resolveTextMenu(target, selection) {
  const el = asElement(target);
  if (!el || el.closest(OPT_OUT_SELECTOR)) return null;

  const field = el.closest('input, textarea');
  let model = null;
  if (field && isTextField(field)) model = fieldModel(field);
  else {
    const host = closestEditable(el);
    model = host ? editableModel(host, selection) : plainModel(el, selection);
  }
  if (!model) return null;
  return Object.values(model.actions).some(Boolean) ? model : null;
}
